import { TenantCountryCodeEnum } from 'probonio-shared-ui/api';
import { getDomainCountryCode, PROBONIO_LINK_CONFIG } from 'probonio-shared-ui/module/links/hooks';
import { useTenant } from 'probonio-shared-ui/module/me';
import { BikeleasingDocument, getBikeleasingDocumentUrl } from 'probonio-shared-ui/utils/bikeleasingDocuments';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BIKELEASING_FAQ, BIKELEASING_FAQ_EN, BIKELEASING_HOW_TO_DIENSTRAD, BIKELEASING_PIM_URL } from '../../constants';

type BikeleasingLanguageCode = 'DE' | 'EN';
type BikeleasingHelpfulResourceTranslationKey =
  'faq' | 'howToDienstrad' | 'leasableAccessories' | 'onlineStationaryProcess' | 'insuranceManual';

interface BikeleasingHelpfulResourceLink {
  translationKey: BikeleasingHelpfulResourceTranslationKey;
  url: string;
}

const getBikeleasingLanguageCode = (language?: string): BikeleasingLanguageCode =>
  language?.toUpperCase().substring(0, 2) === 'DE' ? 'DE' : 'EN';

const getInsuranceManualUrl = (languageCode: BikeleasingLanguageCode, countryCode?: TenantCountryCodeEnum): string => {
  const isAustria = countryCode === TenantCountryCodeEnum.At;

  if (languageCode === 'DE') {
    return `${BIKELEASING_PIM_URL}${
      isAustria ? '/get-asset/PDF/AT/Versicherungshandbuch_at.pdf' : '/get-asset/PDF/DE/Versicherungshandbuch_DE.pdf'
    }`;
  }

  return `${BIKELEASING_PIM_URL}${
    isAustria
      ? '/get-asset/PDF/EN/Austria/insurance-and-service-manual-at-en.pdf'
      : '/get-asset/PDF/EN/Germany/insurance-and-service-manual-de-en.pdf'
  }`;
};

const getFaqUrl = (languageCode: BikeleasingLanguageCode): string => (languageCode === 'DE' ? BIKELEASING_FAQ : BIKELEASING_FAQ_EN);

const useBikeleasingLanguageCode = (): BikeleasingLanguageCode => {
  const { i18n } = useTranslation();
  return getBikeleasingLanguageCode(i18n.resolvedLanguage || i18n.language);
};

export type BikeleasingCalculatorFormType = 'employee' | 'selfemployed' | 'official';

interface UseBikeleasingCalculatorUrlOptions {
  companyCode?: string;
  countryCode?: TenantCountryCodeEnum;
  formType?: BikeleasingCalculatorFormType;
}

export const useBikeleasingCalculatorUrl = ({
  companyCode,
  countryCode,
  formType = 'employee',
}: UseBikeleasingCalculatorUrlOptions): string | undefined => {
  const { i18n } = useTranslation();

  return useMemo(() => {
    const origin = import.meta.env.VITE_BIKELEASING_CALCULATOR_URL;
    if (!origin) {
      return undefined;
    }

    const isAustria = countryCode === TenantCountryCodeEnum.At;
    const url = new URL(isAustria ? '/homepage/at/calculatorat' : '/homepage/de/calculator', origin);
    if (companyCode) {
      url.searchParams.set('code', companyCode);
    }

    if (isAustria && i18n.resolvedLanguage === 'de') {
      url.searchParams.set('lang', 'de-AT');
    } else if (i18n.resolvedLanguage !== 'de') {
      url.searchParams.set('lang', 'en-US');
    }

    if (isAustria && formType === 'selfemployed') {
      url.searchParams.set('form', 'selfemployedat');
    } else if (formType !== 'employee') {
      url.searchParams.set('form', formType);
    }

    return url.toString();
  }, [companyCode, countryCode, formType, i18n.resolvedLanguage]);
};

export const useBikeleasingInformNowUrl = (): string => {
  const domainCountryCode = getDomainCountryCode();
  const bikeleasingLanguageCode = useBikeleasingLanguageCode();
  let path = 'arbeitnehmer';
  if (bikeleasingLanguageCode !== 'DE' && domainCountryCode !== TenantCountryCodeEnum.At) {
    path = 'en/employees';
  }
  return `${PROBONIO_LINK_CONFIG[domainCountryCode].BLS_WEB}${path}`;
};

export const useBikeleasingHelpfulResourceLinks = (): {
  titleTranslationKey: string;
  links: BikeleasingHelpfulResourceLink[];
} => {
  const { tenant } = useTenant();
  const languageCode = useBikeleasingLanguageCode();
  const isAustria = tenant?.countryCode === TenantCountryCodeEnum.At;
  const leasableAccessoriesUrl = getBikeleasingDocumentUrl(BikeleasingDocument.LeasableAccessories, tenant?.countryCode, languageCode);
  const onlineStationaryProcessUrl = getBikeleasingDocumentUrl(
    BikeleasingDocument.OnlineStationaryProcess,
    tenant?.countryCode,
    languageCode,
  );

  return {
    titleTranslationKey: isAustria ? 'bikeleasing_v2.infoPage.resources.titleAT' : 'bikeleasing_v2.infoPage.resources.title',
    links: [
      { translationKey: 'howToDienstrad', url: BIKELEASING_HOW_TO_DIENSTRAD },
      ...(leasableAccessoriesUrl ? [{ translationKey: 'leasableAccessories' as const, url: leasableAccessoriesUrl }] : []),
      ...(onlineStationaryProcessUrl ? [{ translationKey: 'onlineStationaryProcess' as const, url: onlineStationaryProcessUrl }] : []),
      { translationKey: 'insuranceManual', url: getInsuranceManualUrl(languageCode, tenant?.countryCode) },
      { translationKey: 'faq', url: getFaqUrl(languageCode) },
    ],
  };
};
